"use client"

import { useLayoutEffect } from "react"
import Link from "next/link"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { CheckCircleIcon, ClipboardListIcon, CreditCardIcon, UserCircle2Icon } from "lucide-react"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"
import HeaderLogo from "@/components/storyblok/header/HeaderLogo"

interface UBCheckoutHeaderProps {
  blok: any
  isB2BCustomer: boolean
  sbLocale: string
}

interface CheckoutHeaderItem {
  icon: any
  name: string
}

interface UBCheckoutHeaderItemProps {
  blok: any
  icon: any
  name: string
  muted: boolean
  inProgress: boolean
}

function UBCheckoutHeaderItem({ blok, name, muted, inProgress, ...restProps }: UBCheckoutHeaderItemProps) {
  const t = useTranslations("storefront")
  return (
    <div className={"flex flex-row items-center justify-center gap-0"}>
      <div
        className={cn(
          "flex size-6 items-center justify-center rounded-full border-2 border-primary bg-primary sm:size-8 md:size-12",
          {
            "border-muted-foreground bg-white": muted,
          }
        )}
      >
        <restProps.icon
          className={cn("size-4 stroke-1 sm:size-4 md:size-6 lg:size-8", {
            "text-muted-foreground": muted,
            "text-white": !muted,
          })}
        />
      </div>
      <div
        className={cn(
          "relative flex h-9 w-10 flex-col items-center justify-center bg-transparent xs:w-14 sm:w-20 md:w-32 lg:w-40",
          {
            "w-0 sm:w-0 md:w-0": name === "confirmation",
          }
        )}
      >
        <p
          className={cn("absolute top-1 hidden flex-row items-center bg-white px-1 font-bold lg:flex", {
            "text-muted-foreground": muted,
          })}
        >
          {t("checkout.header.item." + name)}
        </p>

        <div
          className={cn("flex h-0.5 w-full flex-row gap-0", {
            hidden: name === "confirmation",
          })}
        >
          <div
            className={cn("h-full w-1/2 bg-primary", {
              "bg-muted-foreground": muted,
            })}
          />
          <div
            className={cn("h-full w-1/2 bg-primary", {
              "bg-muted-foreground": muted || inProgress,
            })}
          />
        </div>
      </div>
    </div>
  )
}

export default function UBCheckoutHeaderClient({ blok, isB2BCustomer, sbLocale }: UBCheckoutHeaderProps) {
  const { setIsB2BCustomer } = useUbStorefrontState()
  const items: CheckoutHeaderItem[] = [
    {
      icon: UserCircle2Icon,
      name: "addresses",
    },
    {
      icon: CreditCardIcon,
      name: "payment",
    },
    {
      icon: ClipboardListIcon,
      name: "review",
    },
    {
      icon: CheckCircleIcon,
      name: "confirmation",
    },
  ]

  let muted = false

  useLayoutEffect(() => {
    setIsB2BCustomer(isB2BCustomer)
  }, [setIsB2BCustomer, isB2BCustomer])

  return (
    /*Why w-screen? See:
     https://github.com/radix-ui/primitives/issues/1496 and
     https://www.reddit.com/r/reactjs/comments/1fjcwkh/comment/lnt3a0j/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button*/
    <div className={cn("z-151 w-full bg-white", {})}>
      <div
        className={cn(
          `
            mx-auto flex min-h-14 max-w-7xl items-start border-b border-gray-200 px-1 py-2
            sm:px-6
            lg:h-[90px] lg:border-0 lg:px-8
          `
        )}
      >
        <div className="flex w-full flex-row items-center gap-2">
          <HeaderLogo
            logo={blok.logo}
            subtext={blok.subtext}
            sbLocale={sbLocale}
            className={"max-w-20 xs:max-w-24 sm:max-w-28 md:max-w-full"}
          />
          <div className={cn("hidden grow flex-row items-center justify-end gap-0 xs:flex")}>
            {items.map((item) => {
              let inProgress = false
              if (item.name === blok.active) {
                inProgress = true
              }

              let ubCheckoutHeaderItem =
                !muted && item.name != blok.active && blok.active !== "confirmation" ? (
                  <Link
                    key={item.name}
                    prefetch={false}
                    className={cn("flex items-center gap-1")}
                    href={"/basket/checkout/" + item.name}
                  >
                    <UBCheckoutHeaderItem
                      blok={blok}
                      icon={item.icon}
                      name={item.name}
                      muted={muted}
                      inProgress={inProgress}
                    />
                  </Link>
                ) : (
                  <div key={item.name} className={cn("flex items-center gap-1")}>
                    <UBCheckoutHeaderItem
                      blok={blok}
                      icon={item.icon}
                      name={item.name}
                      muted={muted && blok.active !== "confirmation"}
                      inProgress={inProgress}
                    />
                  </div>
                )
              if (item.name === blok.active) {
                muted = true
              }
              return ubCheckoutHeaderItem
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
